import { useState, useCallback } from 'react';
import { Receipt } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, fmtAmtZero, fmtPct,
  Delta, Sparkline, Section, EmptyState, CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  AnalyticsGroupBy, ExpenseAnalysisDto, ExpenseAnalysisFilter, ExpenseRow,
} from '@/types/analytics.types';

// ── Options ───────────────────────────────────────────────────────────────────

const GROUP_BY_OPTIONS: Array<{ value: AnalyticsGroupBy; label: string }> = [
  { value: 'CATEGORY', label: 'Expense Category' },
  { value: 'COST_CENTRE', label: 'Cost Centre' },
  { value: 'ACCOUNT', label: 'Expense Account' },
];

const GROUP_BY_LABELS: Record<AnalyticsGroupBy, string> = {
  CATEGORY: 'Expense Category',
  COST_CENTRE: 'Cost Centre',
  ACCOUNT: 'Expense Account',
  LOCATION: 'Branch / Location',
};

// ── Chart tooltip ─────────────────────────────────────────────────────────────

const AmountTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-xs">
      {label && <p className="font-semibold text-gray-700 dark:text-gray-200 mb-2">{label}</p>}
      {payload.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-6">
          <span className="flex items-center gap-1.5" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-gray-900 dark:text-white tabular-nums">
            {fmtAmtZero(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const SliceTooltip = ({ active, payload }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { percentOfTotal: number } }>;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <span className="font-medium text-gray-700 dark:text-gray-200">{p.name}</span>
      <span className="block text-gray-900 dark:text-white font-semibold mt-0.5 tabular-nums">
        {fmtAmtZero(p.value)} · {fmtPct(p.payload.percentOfTotal)}
      </span>
    </div>
  );
};

// ── Excel export ──────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code', header: 'Code', width: 16 },
  { key: 'Name', header: 'Name', width: 34 },
  { key: 'Actual', header: 'Actual', width: 16, numFmt: '#,##0.00' },
  { key: 'MTD', header: 'MTD', width: 16, numFmt: '#,##0.00' },
  { key: 'Previous Month', header: 'Previous Month', width: 16, numFmt: '#,##0.00' },
  { key: 'Change vs Prev Month', header: 'Δ vs Prev Month', width: 16, numFmt: '#,##0.00' },
  { key: 'Change vs Prev Month %', header: 'Δ vs Prev Month %', width: 17 },
  { key: 'Previous Year', header: 'Previous Year', width: 16, numFmt: '#,##0.00' },
  { key: 'Change vs Prev Year', header: 'Δ vs Prev Year', width: 16, numFmt: '#,##0.00' },
  { key: 'Change vs Prev Year %', header: 'Δ vs Prev Year %', width: 17 },
  { key: 'YTD', header: 'YTD', width: 16, numFmt: '#,##0.00' },
  { key: 'Avg Monthly', header: 'Avg Monthly', width: 16, numFmt: '#,##0.00' },
  { key: 'Highest Month', header: 'Highest Month', width: 16, numFmt: '#,##0.00' },
  { key: 'Highest Month Label', header: 'Highest Month Period', width: 18 },
  { key: 'Lowest Month', header: 'Lowest Month', width: 16, numFmt: '#,##0.00' },
  { key: 'Lowest Month Label', header: 'Lowest Month Period', width: 18 },
  { key: 'Contribution%', header: 'Contribution %', width: 14 },
];

function toExcelRow(r: ExpenseRow) {
  return {
    Code: r.groupCode,
    Name: r.groupName,
    Actual: r.actual,
    MTD: r.mtd,
    'Previous Month': r.previousMonth,
    'Change vs Prev Month': r.changeVsPreviousMonth,
    'Change vs Prev Month %': r.changeVsPreviousMonthPercent ?? '',
    'Previous Year': r.previousYear,
    'Change vs Prev Year': r.changeVsPreviousYear,
    'Change vs Prev Year %': r.changeVsPreviousYearPercent ?? '',
    YTD: r.ytd,
    'Avg Monthly': r.averageMonthly,
    'Highest Month': r.highestMonth,
    'Highest Month Label': r.highestMonthLabel ?? '',
    'Lowest Month': r.lowestMonth,
    'Lowest Month Label': r.lowestMonthLabel ?? '',
    'Contribution%': r.contributionPercent,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ExpenseAnalysisPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<ExpenseAnalysisFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    groupBy: 'CATEGORY',
  });
  const [data, setData] = useState<ExpenseAnalysisDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getExpenseAnalysis(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  if (!hasPermission(Permissions.RPT_EXPENSE_ANALYSIS)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    const rows: Record<string, string | number>[] = data.rows.map(toExcelRow);
    rows.push({
      Code: '',
      Name: 'TOTAL',
      Actual: data.totalActual,
      MTD: '',
      'Previous Month': '',
      'Change vs Prev Month': '',
      'Change vs Prev Month %': '',
      'Previous Year': '',
      'Change vs Prev Year': '',
      'Change vs Prev Year %': '',
      YTD: '',
      'Avg Monthly': '',
      'Highest Month': '',
      'Highest Month Label': '',
      'Lowest Month': '',
      'Lowest Month Label': '',
      'Contribution%': 100,
    });
    exportToExcel({
      sheetName: 'Expense Analysis',
      columns: EXCEL_COLUMNS,
      rows,
      title: 'Expense Analysis',
      subtitle: `${data.companyName} · ${GROUP_BY_LABELS[data.groupBy]} · ${filter.fromDate} to ${filter.toDate}`,
    }, `ExpenseAnalysis_${filter.fromDate}_${filter.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Receipt size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Expense Analysis</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Group By</label>
            <select
              value={filter.groupBy ?? 'CATEGORY'}
              onChange={e => setFilter(f => ({ ...f, groupBy: e.target.value as AnalyticsGroupBy }))}
              className={INPUT_CLS}
            >
              {GROUP_BY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <RunReportButton
            onClick={() => { void run(); }}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to generate the Expense Analysis.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Expenses by {GROUP_BY_LABELS[data.groupBy]} — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                {' · '}{data.rows.length} rows
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>

            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1500px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={TH_CLS}>Code / Name</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Actual</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>MTD</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Previous Month</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Δ vs Prev Month</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Previous Year</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Δ vs Prev Year</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>YTD</th>
                    <th className={cn(TH_R_CLS, 'w-28')}>Avg Monthly</th>
                    <th className={cn(TH_R_CLS, 'w-32')}>Highest Month</th>
                    <th className={cn(TH_R_CLS, 'w-32')}>Lowest Month</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Contribution%</th>
                    <th className={cn(TH_R_CLS, 'w-24')}>Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No expenses match the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    data.rows.map((r, i) => (
                      <tr
                        key={r.groupId ?? `${r.groupCode}-${i}`}
                        className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                          <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-2">{r.groupCode}</span>
                          {r.groupName}
                        </td>
                        <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmt(r.actual)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.mtd)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.previousMonth)}</td>
                        <td className="px-3 py-2 text-right">
                          <Delta percent={r.changeVsPreviousMonthPercent} higherIsBetter={false} />
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.previousYear)}</td>
                        <td className="px-3 py-2 text-right">
                          <Delta percent={r.changeVsPreviousYearPercent} higherIsBetter={false} />
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.ytd)}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.averageMonthly)}</td>
                        <td className={AMT_CLS}>
                          <span className="text-gray-700 dark:text-gray-300">{fmtAmt(r.highestMonth)}</span>
                          {r.highestMonthLabel && (
                            <span className="block text-[10px] text-gray-400 dark:text-gray-500">{r.highestMonthLabel}</span>
                          )}
                        </td>
                        <td className={AMT_CLS}>
                          <span className="text-gray-700 dark:text-gray-300">{fmtAmt(r.lowestMonth)}</span>
                          {r.lowestMonthLabel && (
                            <span className="block text-[10px] text-gray-400 dark:text-gray-500">{r.lowestMonthLabel}</span>
                          )}
                        </td>
                        <td className={cn(AMT_CLS, 'text-gray-500 dark:text-gray-400')}>{fmtPct(r.contributionPercent)}</td>
                        <td className="px-3 py-2 text-right">
                          <Sparkline values={r.sparkline} higherIsBetter={false} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                    <td className="px-3 py-2 text-sm font-bold text-gray-900 dark:text-white">TOTAL</td>
                    <td className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>{fmtAmt(data.totalActual)}</td>
                    <td colSpan={11} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Monthly trend */}
          <Section title="Monthly Expense Trend" subtitle="Operating expenses by period">
            {data.monthlyTrend.length === 0 ? (
              <EmptyState message="No trend data for this period." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.monthlyTrend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="expenseTrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLOURS[3]} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={CHART_COLOURS[3]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={80} />
                  <ChartTooltip content={<AmountTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area
                    type="monotone"
                    dataKey="operatingExpenses"
                    name="Operating Expenses"
                    stroke={CHART_COLOURS[3]}
                    strokeWidth={2}
                    fill="url(#expenseTrendFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* Top categories + mix */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Section title="Top Expense Categories" subtitle="Largest spend areas for the period">
              {data.topCategories.length === 0 ? (
                <EmptyState message="No expense categories for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(220, data.topCategories.length * 34)}>
                  <BarChart
                    data={data.topCategories}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={140} />
                    <ChartTooltip content={<AmountTooltip />} />
                    <Bar dataKey="amount" name="Amount" radius={[0, 3, 3, 0]}>
                      {data.topCategories.map((c, i) => (
                        <Cell key={c.id ?? `${c.code}-${i}`} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Section title="Expense Mix" subtitle="Share of total spend">
              {data.topCategories.length === 0 ? (
                <EmptyState message="No expense categories for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.topCategories}
                      dataKey="amount"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={95}
                      paddingAngle={2}
                    >
                      {data.topCategories.map((c, i) => (
                        <Cell key={c.id ?? `${c.code}-${i}`} fill={CHART_COLOURS[i % CHART_COLOURS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip content={<SliceTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Section>
          </div>
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Expense Analysis"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Expense Analysis"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period', value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Grouped By', value: GROUP_BY_LABELS[data.groupBy] },
              { label: 'Total Expenses', value: fmtAmtZero(data.totalActual) },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Code', 'Name', 'Actual', 'MTD', 'Prev. Month', 'Prev. Year', 'YTD', 'Avg Monthly', 'Highest', 'Lowest', 'Contribution%'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i >= 2 ? 'right' : 'left', fontWeight: 700,
                      whiteSpace: 'nowrap', fontSize: '8.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.groupId ?? `${r.groupCode}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#6b7280' }}>{r.groupCode}</td>
                  <td style={{ padding: '3px 6px' }}>{r.groupName}</td>
                  {[r.actual, r.mtd, r.previousMonth, r.previousYear, r.ytd, r.averageMonthly, r.highestMonth, r.lowestMonth].map((v, idx) => (
                    <td key={idx} style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: idx === 0 ? 600 : 400 }}>{fmtAmt(v)}</td>
                  ))}
                  <td style={{ padding: '3px 6px', textAlign: 'right' }}>{fmtPct(r.contributionPercent)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #1e3a5f', background: '#f9fafb' }}>
                <td colSpan={2} style={{ padding: '4px 6px', fontWeight: 700 }}>TOTAL</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmtAmt(data.totalActual)}</td>
                <td colSpan={8} />
              </tr>
            </tfoot>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
